// ドラッグ移動・複製の最終位置決定ロジック（純粋関数）。
//
// 生の移動量から、キャンバス clamp・整列スナップ・（必要なら）グリッド吸着を適用した各フレームの
// 位置と、表示すべき補助線を求める。プレビューと確定で同じ計算を共有し「見た目」と「確定値」を
// 一致させる。React / DOM / store へ依存しないためテストしやすい。
//
// グリッド吸着は **グループ全体へ共通の補正を 1 回だけ** 適用する（外接矩形の左上を 10px グリッドへ
// 揃える）。各オブジェクトを個別に丸めると、グリッドに乗っていない複数選択の相対配置が崩れるため、
// 貼り付け経路（canvas-stage.tsx）と同じく相対配置を保つ。整列が成立した軸はグリッドより整列を優先する。

import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "@refinement-canvas/domain";
import { CANVAS_SNAP_PX } from "./canvas-coordinates";
import { computeAlignmentSnap, type AlignmentBox, type AlignmentGuide } from "./canvas-alignment";

/** 移動対象 1 件の初期位置・サイズ（左上 + 幅高さ）。 */
export interface DragFrame {
  readonly baseLeft: number;
  readonly baseTop: number;
  readonly baseWidth: number;
  readonly baseHeight: number;
}

export interface ResolveDragInput {
  readonly frames: readonly DragFrame[];
  readonly rawDx: number;
  readonly rawDy: number;
  readonly staticBoxes: readonly AlignmentBox[];
  /** スナップ判定しきい値（ステージ px。通常は screen px / zoom）。 */
  readonly threshold: number;
  readonly gridSnapEnabled: boolean;
  /** 確定時は true（グリッド吸着を適用）、プレビュー時は false。 */
  readonly applyGrid: boolean;
  /** 整列スナップ（スマートガイド）を有効にするか。回転オブジェクト移動時は false。 */
  readonly alignEnabled: boolean;
}

export interface ResolveDragOutput {
  /** frames と同順の確定（またはプレビュー）位置。 */
  readonly positions: readonly { readonly x: number; readonly y: number }[];
  readonly guides: readonly AlignmentGuide[];
}

export function resolveDragPositions(input: ResolveDragInput): ResolveDragOutput {
  const { frames, rawDx, rawDy, staticBoxes, threshold, gridSnapEnabled, applyGrid, alignEnabled } =
    input;

  // まずキャンバス境界内へ収まる移動量へ clamp する。
  const base = clampDeltaForFrames(frames, rawDx, rawDy);

  // 移動集合の外接矩形（base 適用後）を整列対象として扱う。回転を含む移動では整列を無効化する。
  const union = unionBox(frames, base.dx, base.dy);
  const alignment = alignEnabled
    ? computeAlignmentSnap(union, staticBoxes, threshold)
    : { snapDx: 0, snapDy: 0, matchedX: false, matchedY: false, guides: [] as AlignmentGuide[] };

  // グループ全体へ共通の差分として、整列スナップ + グリッド補正をまとめて適用する。
  let totalDx = base.dx + alignment.snapDx;
  let totalDy = base.dy + alignment.snapDy;
  if (applyGrid && gridSnapEnabled) {
    // 整列が成立した軸は整列を優先し、非成立軸のみ外接矩形の左上をグリッドへ揃える（相対配置を保つ）。
    const alignedUnionLeft = union.x + alignment.snapDx;
    const alignedUnionTop = union.y + alignment.snapDy;
    if (!alignment.matchedX) totalDx += snapToGrid(alignedUnionLeft) - alignedUnionLeft;
    if (!alignment.matchedY) totalDy += snapToGrid(alignedUnionTop) - alignedUnionTop;
  }

  // 補正後の共通差分を再度 clamp し、グループの剛体性（相対配置）を保ったまま境界内へ収める。
  const clamped = clampDeltaForFrames(frames, totalDx, totalDy);
  const positions = frames.map((frame) => ({
    x: frame.baseLeft + clamped.dx,
    y: frame.baseTop + clamped.dy,
  }));

  // キャンバス境界 clamp で最終位置が整列意図からずれた場合、ガイドの位置（固定座標）も長さ
  // （start/end）も実際の描画とずれる。補助線は一時的な表示であり、ずれた線を出すより非表示が
  // 安全なため、いずれかの軸が clamp で削られたらガイドを一切出さない。clamp が効かない通常時は
  // 全ガイドを残す。
  const clampedAny =
    Math.abs(clamped.dx - totalDx) > 1e-6 || Math.abs(clamped.dy - totalDy) > 1e-6;
  const guides = clampedAny ? [] : alignment.guides;

  return { positions, guides };
}

/** 移動集合の外接矩形を求める（各フレームを dx,dy だけ動かした後の合併）。 */
function unionBox(frames: readonly DragFrame[], dx: number, dy: number): AlignmentBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const frame of frames) {
    const left = frame.baseLeft + dx;
    const top = frame.baseTop + dy;
    minX = Math.min(minX, left);
    minY = Math.min(minY, top);
    maxX = Math.max(maxX, left + frame.baseWidth);
    maxY = Math.max(maxY, top + frame.baseHeight);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** グループ共通の移動量を、全フレームがキャンバス内へ収まる範囲へ clamp する。 */
export function clampDeltaForFrames(
  frames: readonly DragFrame[],
  dx: number,
  dy: number
): { readonly dx: number; readonly dy: number } {
  const minDx = Math.max(...frames.map((frame) => -frame.baseLeft));
  const maxDx = Math.min(
    ...frames.map((frame) => CANVAS_WIDTH_PX - (frame.baseLeft + frame.baseWidth))
  );
  const minDy = Math.max(...frames.map((frame) => -frame.baseTop));
  const maxDy = Math.min(
    ...frames.map((frame) => CANVAS_HEIGHT_PX - (frame.baseTop + frame.baseHeight))
  );
  return {
    dx: clampToRange(dx, minDx, maxDx),
    dy: clampToRange(dy, minDy, maxDy),
  };
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}

function clampToRange(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (max < min) return 0;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
